import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { hash } from "@ember/helper";
import { Task, task } from "ember-concurrency";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { array, func, number, bool, instanceOf } from "prop-types";
import { on } from "@ember/modifier";
import { not, or } from "ember-truth-helpers";
import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";
import GButton from "glesys-controlpanel/components/g-button";
import { t } from "ember-intl";
import Box from "glesys-controlpanel/components/box";

const WizardNavigation = <template>
  <div class="flex gap-2 items-center justify-between" data-test-wizard-navigation>
    <div>
      {{#if @showBack}}
        <GButton data-test-wizard-back-button @color="gray" {{on "click" @onBack}}>
          {{t "button.back"}}
        </GButton>
      {{/if}}
    </div>
    <div>
      {{#if @showNext}}
        <GButton
          data-test-wizard-next-button
          @color="blue"
          @disabled={{@nextDisabled}}
          @loading={{@nextLoading}}
          @type="submit"
        >
          {{@nextLabel}}
        </GButton>
      {{/if}}
      {{#if @showFinish}}
        <GButton
          data-test-wizard-finish-button
          @color="blue"
          @disabled={{@finishDisabled}}
          @loading={{@finishLoading}}
          @type="submit"
        >
          {{@finishLabel}}
        </GButton>
      {{/if}}
    </div>
  </div>
</template>;

@forbidExtraArgs
export default class Wizard extends Component {
  @service intl;

  @arg(array.isRequired) steps;
  @arg(number) initialStep = 0;
  @arg(instanceOf(Task)) onNext;
  @arg(bool) nextLoading = false;
  @arg(instanceOf(Task)) onFinish;
  @arg(func) onChange;
  @arg(bool) finishLoading = false;
  @arg(bool) showNavigation = true;

  @tracked currentStepIndex = this.initialStep;

  get currentStep() {
    return this.steps[this.currentStepIndex];
  }

  get stepCounter() {
    return this.intl.t("label.wizard-steps", {
      current: this.currentStepIndex + 1,
      total: this.steps.length,
    });
  }

  get isFirstStep() {
    return this.currentStepIndex === 0;
  }

  get isLastStep() {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get canGoNext() {
    if (this.currentStep?.canProceed !== undefined) {
      return this.currentStep.canProceed;
    }
    return true;
  }

  get canGoBack() {
    if (this.currentStep?.allowBack === false) {
      return false;
    }

    return !this.isFirstStep;
  }

  get nextButtonLabel() {
    return this.currentStep?.nextButtonLabel || this.intl.t("button.next");
  }

  get finishButtonLabel() {
    return this.currentStep?.finishButtonLabel || this.intl.t("button.finish");
  }

  get shouldShowFinish() {
    return this.isLastStep;
  }

  @action
  reset() {
    this.currentStepIndex = this.initialStep;
  }

  callStepHandler(handler) {
    return handler?.linked().perform(this.currentStepIndex, this.currentStep);
  }

  handleNext = task(async () => {
    const shouldProceed = await this.callStepHandler(this.onNext);

    if (shouldProceed !== false) {
      this.currentStepIndex++;
    }
  });

  handleFinish = task(async () => {
    await this.callStepHandler(this.onFinish);
  });

  @action
  handleBack() {
    if (this.canGoBack) {
      this.currentStepIndex--;
    }
  }

  @action
  handleChange(event) {
    this.onChange?.(event);
  }

  @action
  handleSubmit(event) {
    event.preventDefault();

    if (this.isLastStep) {
      this.handleFinish.perform();
    } else {
      this.handleNext.perform();
    }

    return false;
  }

  <template>
    <form
      class="w-full"
      data-test-wizard
      {{mergeClasses}}
      {{on "input" this.handleChange}}
      {{on "submit" this.handleSubmit}}
      ...attributes
    >
      <Box>
        <:header>
          <div class="flex items-center justify-between w-full">
            <div data-test-wizard-step-title>
              {{this.currentStep.title}}
            </div>
            <div class="text-sm text-gray-500 font-normal" data-test-wizard-step-counter>
              {{this.stepCounter}}
            </div>
          </div>
        </:header>
        <:default>
          {{yield this.currentStepIndex (hash reset=this.reset)}}
        </:default>

        <:footer>
          {{#if this.showNavigation}}
            <WizardNavigation
              @showBack={{this.canGoBack}}
              @showNext={{not this.shouldShowFinish}}
              @showFinish={{this.shouldShowFinish}}
              @nextDisabled={{or (not this.canGoNext) this.nextLoading}}
              @finishDisabled={{or (not this.canGoNext) this.finishLoading}}
              @finishLoading={{this.finishLoading}}
              @nextLoading={{this.nextLoading}}
              @finishLabel={{this.finishButtonLabel}}
              @nextLabel={{this.nextButtonLabel}}
              @onBack={{this.handleBack}}
            />
          {{/if}}
        </:footer>
      </Box>
    </form>
  </template>
}
